import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import CopyableText from 'components/atoms/buttons/copyable-text';
import CopyIcon from 'components/atoms/icons/copyIcon';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';
import { capitalize } from 'utils';
import CompletedTag from 'components/atoms/tags/CompletedTag';

import NoteCell from '../note-cell';
import s from './styles.module.scss';
import DueDateCell from '../due-date-cell';

import type {
  IKeyNoteData,
  IFollowUpTableData,
  IFollowUpTableDataFilters,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

interface Props {
  filter: IFollowUpTableDataFilters;
}

const COMPLETED_SECONDS = 60 * 60 - 1;

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<IFollowUpTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  return [
    {
      label: i18next.t('labels.name'),
      key: 'name',
      width: '220px',
      dataCy: 'follow-up-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['f-name'] ? tableSortMap[filter['f-name']] : undefined,
      render(name: string, record) {
        return (
          <div className={s['followup-patient-tag-wrapper']}>
            <Link to={Routes.patientGuid(record.guid)} className={s.link}>
              {name}
            </Link>
            {(record.ccmActionTimes ?? 0) > COMPLETED_SECONDS &&
              record.ccmInteractionStatus &&
              record.hasOnlyMessageResponse !== true && <CompletedTag small />}
          </div>
        );
      },
    },
    {
      label: i18next.t('labels.ptID'),
      key: 'guid',
      width: '80px',
      align: 'center',
      dataCy: 'follow-up-patients-guid',
      render(guid: string) {
        return (
          <CopyableText
            wrapperClassName={s.copyable}
            value={guid}
            label="Copy"
            icon={<CopyIcon />}
          />
        );
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '190px',
      align: 'left',
      dataCy: 'follow-up-patients-provider',
      sortable: true,
      sortOrder: filter['f-provider']
        ? tableSortMap[filter['f-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.callsMonth'),
      key: 'callsMonth',
      width: '70px',
      align: 'center',
      dataCy: 'follow-up-patients-calls-month',
      sortable: true,
      sortOrder: filter['f-callsMonth']
        ? tableSortMap[filter['f-callsMonth']]
        : undefined,
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      width: '120px',
      align: 'center',
      dataCy: 'follow-up-patients-due-date',
      sortable: true,
      sortOrder: filter['f-dueDate']
        ? tableSortMap[filter['f-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      width: '95px',
      align: 'center',
      dataCy: 'follow-up-patients-appointment-date',
      sortable: true,
      sortOrder: filter['f-appointmentDate']
        ? tableSortMap[filter['f-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return DateFormatter({
          date: appointmentDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.ccmInteractionStatus'),
      key: 'ccmInteractionStatus',
      width: '40px',
      align: 'center',
      dataCy: 'follow-up-patients-ccmInteractionStatus',
      sortable: true,
      sortOrder: filter['f-ccmInteractionStatus']
        ? tableSortMap[filter['f-ccmInteractionStatus']]
        : undefined,
      render(ccmInteractionStatus: boolean) {
        return capitalize(String(ccmInteractionStatus));
      },
    },
    {
      label: i18next.t('labels.type'),
      key: 'type',
      width: '75px',
      align: 'center',
      dataCy: 'follow-up-patients-type',
      sortable: false,
      sortOrder: filter['f-type'] ? tableSortMap[filter['f-type']] : undefined,
    },
    {
      label: i18next.t('labels.lastBilled'),
      key: 'lastBilledDate',
      width: '85px',
      align: 'center',
      dataCy: 'follow-up-patients-last-billed-date',
      sortable: false,
      sortOrder: filter['f-lastBilledDate']
        ? tableSortMap[filter['f-lastBilledDate']]
        : undefined,
      render(lastBilledDate: string) {
        return DateFormatter({
          date: lastBilledDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.smsPreferred'),
      key: 'smsPreferred',
      width: '60px',
      align: 'center',
      dataCy: 'follow-up-patients-sms-preferred',
      render(smsPreferred: boolean) {
        return smsPreferred
          ? i18next.t('controls.yes')
          : i18next.t('controls.no');
      },
    },
    {
      label: i18next.t('labels.interactiveTime'),
      key: 'actionTimes',
      width: '75px',
      align: 'center',
      dataCy: 'follow-up-patients-interactive-time',
      sortable: true,
      sortOrder: filter['f-actionTimes']
        ? tableSortMap[filter['f-actionTimes']]
        : undefined,
    },
    {
      label: i18next.t('labels.notes'),
      width: '200px',
      key: 'notes',
      render(notes: IKeyNoteData[], record) {
        const note = notes.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )[0];
        return (
          <NoteCell
            text={note?.note ?? ''}
            onClick={() => {
              setTableNote({
                notes,
                tableName: 'follow-up',
                patientGuid: record.guid,
                patientName: record.name,
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'follow-up-patients-notes',
    },
  ];
};
